import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/toast';
import { apiGet } from '@/lib/api/axios';
import useAuth from '@/hooks/useAuth';
import { useEstimateCostedActionCost, toCostEstimateError, toCostEstimateResult } from 'src/features/pam/api/hooks';
import * as api from '../api';
import {
  actionCanAddUpdateOrResolve,
  actionCanArchive,
  actionCanEdit,
  actionCanResolve,
  actionCanUnarchive,
  actionCanUnresolve
} from '../permissions';
import type { Action, ActionCreate, ActionUpdate, Group, OrganizationUser } from '../types';
import { ActionModal } from './ActionModal';
import { MoveActionModal } from './MoveActionModal';

const ActionViewerContext = createContext<((actionId: string) => void) | null>(null);

/** Open an action in the page-level modal. Null outside `ActionViewerProvider`. */
export function useOpenAction(): ((actionId: string) => void) | null {
  return useContext(ActionViewerContext);
}

export function ActionViewerProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const estimateActionCost = useEstimateCostedActionCost();
  const [action, setAction] = useState<Action | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [movingAction, setMovingAction] = useState<Action | null>(null);
  const [isMoving, setIsMoving] = useState(false);

  const usersQuery = useQuery({
    queryKey: ['tenant-members', 'action-viewer'],
    queryFn: async () => {
      const response = await apiGet<OrganizationUser[]>('/tenant-management/members', {
        meta: { globalError: false, context: 'action-viewer-users' }
      });
      return response.data;
    },
    enabled: isOpen,
    staleTime: 60_000
  });

  const groupsQuery = useQuery({
    queryKey: ['groups', 'action-viewer'],
    queryFn: async () => (await api.getGroups()).groups,
    enabled: isOpen,
    staleTime: 60_000
  });

  const groups = groupsQuery.data ?? [];

  const refreshLists = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['actions'] });
  }, [queryClient]);

  const openAction = useCallback(async (actionId: string) => {
    try {
      const loaded = await api.getAction(actionId);
      setAction(loaded);
      setIsOpen(true);
    } catch (err: unknown) {
      const description = (err as { parsed?: { description?: string } })?.parsed?.description;
      toast.error(description || 'Failed to load action');
    }
  }, []);

  const handleSave = async (data: ActionCreate | ActionUpdate) => {
    if (!action) return;
    setIsSaving(true);
    try {
      const updated = await api.updateAction(action.id, data as ActionUpdate);
      setAction(updated);
      toast.success('Action updated');
      refreshLists();
    } catch (err: unknown) {
      const description = (err as { parsed?: { description?: string } })?.parsed?.description;
      toast.error(description || 'Failed to save action');
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitResolution = async (current: Action, documentIds: string[], text?: string) => {
    const resolved = await api.resolveAction(current.id, documentIds, text);
    setAction(resolved);
    refreshLists();
  };

  const groupForAction = (current: Action): Group | undefined =>
    groups.find((group) => group.id === current.group_id) ??
    groups.find((group) => group.short_reference === current.short_reference);

  const openActionValue = useMemo(() => openAction, [openAction]);

  return (
    <ActionViewerContext.Provider value={openActionValue}>
      {children}
      <ActionModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        action={action}
        users={usersQuery.data ?? []}
        currentUserId={user?.user_id}
        isLoading={isSaving}
        isEditable={action ? actionCanEdit(action) : false}
        canAddUpdates={action ? actionCanAddUpdateOrResolve(action) : false}
        groups={groups}
        groupLabel={action ? groupForAction(action)?.name : undefined}
        groupResourceId={action ? groupForAction(action)?.id : undefined}
        onEstimateCost={
          action?.can_estimate_cost
            ? async () => {
                try {
                  return toCostEstimateResult(await estimateActionCost.mutateAsync(action.id));
                } catch (err) {
                  throw toCostEstimateError(err);
                }
              }
            : undefined
        }
        onResolve={
          action && actionCanResolve(action)
            ? async (current, payload) => {
                try {
                  await handleSubmitResolution(current, payload.documentIds, payload.text);
                } catch (err: unknown) {
                  const description = (err as { parsed?: { description?: string } })?.parsed?.description;
                  toast.error(description || 'Failed to resolve action');
                  throw err;
                }
              }
            : undefined
        }
        onUnresolve={
          action && actionCanUnresolve(action)
            ? async (current, payload) => {
                const reopened = await api.unresolveAction(current.id, payload.documentIds, payload.text);
                setAction(reopened);
                refreshLists();
              }
            : undefined
        }
        onArchive={
          action && actionCanArchive(action)
            ? async (current) => {
                const archived = await api.archiveAction(current.id);
                setAction(archived);
                refreshLists();
              }
            : undefined
        }
        onUnarchive={
          action && actionCanUnarchive(action)
            ? async (current) => {
                const unarchived = await api.unarchiveAction(current.id);
                setAction(unarchived);
                refreshLists();
              }
            : undefined
        }
        onMove={action && actionCanEdit(action) ? (current) => setMovingAction(current) : undefined}
      />
      <MoveActionModal
        isOpen={movingAction != null}
        action={movingAction}
        groups={groups}
        currentGroupId={movingAction?.group_id}
        onClose={() => {
          if (!isMoving) setMovingAction(null);
        }}
        isSubmitting={isMoving}
        onSubmit={async (targetGroupId) => {
          if (!movingAction) return;
          setIsMoving(true);
          try {
            const moved = await api.moveAction(movingAction.id, targetGroupId);
            setAction(moved);
            setMovingAction(null);
            const targetName = groups.find((group) => group.id === targetGroupId)?.name;
            toast.success(targetName ? `Action moved to ${targetName}` : 'Action moved');
            refreshLists();
          } catch (err: unknown) {
            const description = (err as { parsed?: { description?: string } })?.parsed?.description;
            toast.error(description || 'Failed to move action');
            throw err;
          } finally {
            setIsMoving(false);
          }
        }}
      />
    </ActionViewerContext.Provider>
  );
}
